import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountPermissionForm, { AccountPermissionFormRef } from './AccountPermissionForm';
import useSubAccountPermissions from '@/hooks/useApi/useSubAccountPermissions';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

import { Button } from 'UI';
import styles from './AccountPermissionsDrawer.module.scss';

import { SideDrawer, Box, BodyText } from 'vopay';

type FC = FunctionComponent<AccountPermissionsDrawerProps>;

type AccountPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  accountId?: string;
  accountName?: string;
};

const AccountPermissionsDrawer: FC = ({ isOpen, onClose, accountId, accountName }) => {
  const { permissions, loadingPermissions, fetchSubAccountPermissions, saveSubAccountPermissions } =
    useSubAccountPermissions();
  const formRef = useRef<AccountPermissionFormRef | null>(null);
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Permissions' });

  // Track form state for button disabling
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!accountId || !isOpen) return;

      try {
        setLoading(true);
        await fetchSubAccountPermissions({ SubaccountID: accountId });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.fetchPermissions');
        alertDialog.error(t('errors.title'), errorMessage);
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [accountId, isOpen]);

  const handleSave = async (values: Record<string, boolean | string | number>) => {
    if (!accountId) return;

    try {
      setLoading(true);
      // Convert the values object to the format expected by the API
      const permissions = Object.entries(values).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, string | number | boolean>,
      );

      await saveSubAccountPermissions({
        SubaccountID: accountId,
        ...permissions,
      });
      onClose();
      toast.success(t('success.permissionsUpdated'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.savePermissions');
      alertDialog.error(t('errors.title'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableAll = () => {
    if (formRef.current) {
      formRef.current.enableAll();
    }
  };

  const handleFormStateChange = (hasChanges: boolean, isValid: boolean) => {
    setHasChanges(hasChanges);
    setIsValid(isValid);
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col">
          <span>{t('drawer.title')}</span>
          {accountName && <BodyText weight="md">{accountName}</BodyText>}
        </Box>
      }
      footer={
        <Box jc="fs" gap="sm" className={styles.footer}>
          {!isSearching && (
            <Button onClick={handleEnableAll} colorScheme="secondary" className={styles.enableAllButton}>
              {t('drawer.enableAllButton')}
            </Button>
          )}
          <Button
            disabled={loadingPermissions || !hasChanges || !isValid}
            colorScheme="primary"
            className={styles.saveButton}
            form="accountPermissionForm"
            type="submit"
          >
            {t('drawer.saveButton')}
          </Button>
        </Box>
      }
    >
      {!loadingPermissions && (
        <AccountPermissionForm
          ref={formRef}
          onAccountPermissionSave={handleSave}
          permissions={permissions}
          onFormStateChange={handleFormStateChange}
          onSearchChange={setIsSearching}
        />
      )}
    </SideDrawer>
  );
};

export default AccountPermissionsDrawer;
